import { useState, useEffect } from 'react'
import { HelpCircle, Save, Loader2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { subscriptionApi, dashboardApi } from '@/services/api'
import api from '@/services/api'

export function DefaultPublication() {
  const queryClient = useQueryClient()
  const [selectedFeed, setSelectedFeed] = useState('')

  // Fetch user's subscribed feeds
  const { data: subscription, isLoading: subsLoading } = useQuery({
    queryKey: ['currentSubscription'],
    queryFn: async () => {
      const response = await subscriptionApi.getCurrentSubscription()
      return response.data
    },
  })

  // Fetch current default publication
  const { data: defaultPub, isLoading: defaultLoading } = useQuery({
    queryKey: ['defaultPublication'],
    queryFn: async () => {
      try {
        const response = await api.get('/api/v1/news/default/publication/')
        return response.data
      } catch (error) {
        if (error.response?.status === 404) {
          return null // Not set yet
        }
        throw error
      }
    },
  })

  // Set selected feed when default publication loads
  useEffect(() => {
    if (defaultPub?.default) {
      setSelectedFeed(defaultPub.default)
    }
  }, [defaultPub])

  // Update default publication mutation
  const updateMutation = useMutation({
    mutationFn: async (feedId) => {
      return api.post('/api/v1/news/default/publication/', {
        default: feedId
      })
    },
    onSuccess: () => {
      toast.success('Default publication updated successfully!')
      queryClient.invalidateQueries(['defaultPublication'])
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update default publication')
    }
  })

  const handleSave = () => {
    if (!selectedFeed) {
      toast.error('Please select a publication')
      return
    }
    updateMutation.mutate(selectedFeed)
  }

  const subscribedFeeds = subscription?.feeds || []

  if (subsLoading || defaultLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  if (!subscription?.has_access) {
    return (
      <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
        <p className="text-gray-600 text-center">
          You need an active subscription to set a default publication.
        </p>
      </div>
    )
  }

  if (subscribedFeeds.length === 0) {
    return (
      <div className="p-6 border border-yellow-200 rounded-lg bg-yellow-50">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-1">No Feeds Selected</h3>
            <p className="text-sm text-yellow-800 mb-3">
              You haven't selected any feeds yet. Please add feeds to your subscription first before setting a default publication.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/dashboard/feed'}
              className="border-yellow-600 text-yellow-700 hover:bg-yellow-100"
            >
              Go to Feed Selection
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-xl font-bold text-gray-900">Default Publication</h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-5 h-5 text-gray-400 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>
                Your Default Publication is where we send your subscription money if you read
                zero minutes in a given billing period. This ensures your subscription always
                supports a publication you care about.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="default-pub">Select Publication</Label>
          <Select value={selectedFeed} onValueChange={setSelectedFeed}>
            <SelectTrigger id="default-pub">
              <SelectValue placeholder="-- Select a publication --" />
            </SelectTrigger>
            <SelectContent>
              {subscribedFeeds.map((feed) => (
                <SelectItem key={feed.alias} value={feed.alias}>
                  {feed.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500">
            Choose from your subscribed publications
          </p>
        </div>

        <Button
          onClick={handleSave}
          disabled={!selectedFeed || updateMutation.isLoading}
          className="w-full sm:w-auto"
        >
          {updateMutation.isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Default Publication
            </>
          )}
        </Button>

        {defaultPub?.default && (
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm text-slate-800">
              <strong>Current default:</strong>{' '}
              {subscribedFeeds.find(f => f.alias === defaultPub.default)?.display_name || 'Unknown'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
